import videojs from 'video.js';

import { QueryStringParamProvider } from '../../implementations/player-page/helpers.js';
import { getInteractionColorsFromColor } from '../../util';
import Hotspot360 from '../conditional/360/hotspot-360.js';
import './hotspots.css';

let _shouldRenderHotspots;
let _isCurrentVideo360;
let _renderedHotspots = [];
let _hotspotInfos = [];
let _hotspotsComponent;

/** @type {Hotspot360} */
let hotspot360;

const HOTSPOT_DEFAULT_COLOR = "#02528A";
const HOTSPOT_DEFAULT_OPACITY = 0.75;
/**
 * The Hotspots plugin, which handles adding and rendering the hotspots
 * @extends {external:"videojs.Plugin"}
 **/
class HotspotsPlugin extends videojs.getPlugin('plugin') {
	constructor(player, options) {
		super(player, options);

		hotspot360 = new Hotspot360(player);

		const paramProvider = new QueryStringParamProvider(location.search);
		_shouldRenderHotspots = (paramProvider.getParamCaseSensitive("hotspots") !== "false");

		this.createHotspotHolder();

		this.player.one('loadedmetadata', () => {
			if (isNaN(this.aspect) || !this.aspect) {
				this.aspect = this.player.videoWidth() / this.player.videoHeight();
			}
		});
	}

	setIs360(is360) {
		_isCurrentVideo360 = is360;
	}

	/**
	 * Creates the DOM element to hold the hotspots in that matches the size of the primary stream
	*/
	createHotspotHolder() {
		const Component = videojs.getComponent('Component');
		const hotspotsComponent = new Component(this.player, { name: 'Hotspots' });
		hotspotsComponent.addClass('mediasite-player__hotspots_wrapper');
		hotspotsComponent.addClass('vjs-hidden');

		const hotspotsContainer = document.createElement('div');
		hotspotsContainer.classList.add('mediasite-player__hotspots_container');

		hotspotsComponent.el().appendChild(hotspotsContainer);

		this.player.ready(() => {
			this.player.layout().addChild(hotspotsComponent);
		});

		_hotspotsComponent = hotspotsComponent;
	}

	/**
	 * Creates the timed events for showing/hiding the hotspots on the presentation and then renders them
	 * @param {any} hotspots
	 */
	add(hotspots) {
		if (!_shouldRenderHotspots) {
			return;
		}

		if (hotspots.length === 0) {
			return;
		}

		for (let index = 0; index < hotspots.length; index++) {
			const hotspot = hotspots[index];
			const timedId = this.player.timedEvents().add(
				{
					time: hotspot.StartTime / 1000,
					duration: (hotspot.EndTime / 1000) - (hotspot.StartTime / 1000),
					type: 'hotspot'
				},
				this.showHotspot.bind(this),
				this.hideHotspot.bind(this)
			);

			hotspot.TimedId = timedId;			

			//convert opacity from percentage into decimal
			if (hotspot.Opacity > 1) {
				hotspot.Opacity = hotspot.Opacity / 100;
			}

			_hotspotInfos[timedId] = hotspot;
		}

		//depending if we have XR loaded, either render through XR or load it to the DOM
		//since we've render hotspots, let's activate the raycaster to check if the user looks at them
		if (_isCurrentVideo360) {
			const xr = this.player.xr();

			//'hasPlayerLoadedXR' gets set to true when 'initialized' is triggered in the xr plugin
			//And so, if 'initialized' has already been trigged, add hotspots now. If it hasn't, wait until it has been triggered then do it
			if (xr.hasPlayerLoadedXR) {
				this.#createHotspotsin360(hotspots);
			} else {
				xr.one("initialized", () => {
					this.#createHotspotsin360(hotspots);
				});		
			}
		} else {
			hotspots.forEach((hotspot) => {
				_renderedHotspots[hotspot.TimedId] = this.renderHotspot(hotspot);
			});
		}

		_hotspotsComponent.removeClass('vjs-hidden');
	}

	#createHotspotsin360(hotspots) {
		hotspots.forEach((hotspot) => {
			switch (hotspot.Type) {
				case "Text":
					_renderedHotspots[hotspot.TimedId] = hotspot360.createTextHotspot(hotspot);
					break;				
				case "Button":
					_renderedHotspots[hotspot.TimedId] = hotspot360.createButtonHotspot(hotspot);
					break;
				case "Hotspot":
				default:
					_renderedHotspots[hotspot.TimedId] = hotspot360.createAreaHotspot(hotspot);
					break;
			}
			
		});
	}

	// Displays the hotspot with the id given by the timed event
	showHotspot(id) {
		const targetHotspot = _renderedHotspots[id];
		if (!targetHotspot) {
			console.log(`Attempted to render a hotspot with id ${id} and failed.`);
			return;
		}
		const hotspotInfo = _hotspotInfos[id];

		if (hotspotInfo.HasFocus) {
			//if we're seeking when we trigger the interaction, wait until seeking is done before pausing the player
			if (this.player.seeking()) {
				this.player.one("seeked", () => {
					this.player.pause();
				});
			} else {
				this.player.pause();
			}
		}

		if (_isCurrentVideo360) {
			switch (hotspotInfo.Type) {
				case "Text":
				case "Button":
					hotspot360.updatePositionOfRelativeHotspots();
					break;
				case "Hotspot":
				default:					
					break;
			}

			targetHotspot.visible = true;			
		} else {
			targetHotspot.classList.remove('vjs-hidden');
		}
	}

	// Hides the hotspot with the id given by the timed event
	hideHotspot(id) {
		const targetHotspot = _renderedHotspots[id];
		if (_isCurrentVideo360) {
			targetHotspot.visible = false;
		} else {
			targetHotspot.classList.add('vjs-hidden');
		}
	}

	// Triggers the event associated with the hotspot with said id
	triggerHotspot(id) {
		const hotspotInfo = _hotspotInfos[id];
		const effect = hotspotInfo.InteractionEffect;
		const parameter = hotspotInfo.InteractionParam;

		if (!parameter) {
			return;
		}

		//Deduce what the effect of the hotspot should be then call some sort of function with the parameter that was passed in
		switch (effect) {
			case "timeChange":				
				const targetTime = parameter / 1000;
				this.player.currentTime(targetTime);
				this.player.play();
				break;
			case "presentationChange":
				this.player.loader().loadNewPresentation(parameter);
				break;
			case "urlChange":
				this.player.pause();
				window.open(parameter, '_blank');
				if (_isCurrentVideo360) {
					//after we trigger it, let's disable it in 360 so that we don't keep on triggering it when we move to a new tab
					this.setDisableHotspot(id, true);

					let modalBtns = [];
					modalBtns.push({
						text: "Resume Presentation",
						icon: "0xf101",
						action: this.handleURlChangeResumeButton.bind(this)
					});

					this.player.video360?.().displayModal("Opended a URL in a new tab.", modalBtns);

					this.player.one('play', () => {
						this.player.video360?.().removeModal();
						this.setDisableHotspot(id, false);
					});
				}
				break;
			default:
				this.player.log(`A hotspot with an unknown interaction effect of ${effect} has been triggered.`);
		}
	}

	// allows a hotspot to be seen, but not interactable. Useful if a hotspot should only be triggered once
	setDisableHotspot(id, isDisabled) {
		const targetHotspot = _renderedHotspots[id];
		//Right now, there is no non-360 need to disable a hotspot
		if (_isCurrentVideo360) {
			targetHotspot.disabled = isDisabled;
		}
	}

	handleURlChangeResumeButton() {
		this.player.play();
	}

	/**
	 * Creates a DOM element for the hotspot. Update when non-circle sizing exists, along with any additional properities to a hotspot
	 * @param {any} hotspot
	 */
	renderHotspot(hotspot) {
		let hotspotDOM = document.createElement('div');
		hotspotDOM.classList.add('mediasite-player__hotspot', 'vjs-hidden');
		hotspotDOM.style.setProperty('left', `${hotspot.X * 100}%`);
		hotspotDOM.style.setProperty('top', `${hotspot.Y * 100}%`);
		hotspotDOM.setAttribute("aria-live", "polite");

		switch (hotspot.Type) {
			case "Button":
				hotspotDOM = this.#renderButtonHotspot(hotspot, hotspotDOM);
				const hotspotButtonDOM = hotspotDOM.querySelector(".mediasite-player__hotspot-button");
				hotspotButtonDOM.addEventListener("click", () => {
					this.triggerHotspot(hotspot.TimedId);
				});
				hotspotButtonDOM.addEventListener("keydown", (event) => {
					if (event.key.toLowerCase() === 'enter' || event.key.toLowerCase() === 'space') {
						this.triggerHotspot(hotspot.TimedId);
					}
				});
				break;
			case "Text":
				hotspotDOM = this.#renderTextHotspot(hotspot, hotspotDOM);
				break;
			case "Hotspot":
			default:
				hotspotDOM = this.#renderAreaHotspot(hotspot, hotspotDOM);	
				hotspotDOM.addEventListener("click", () => {
					this.triggerHotspot(hotspot.TimedId);
				});
				hotspotDOM.addEventListener("keydown", (event) => {
					if (event.key.toLowerCase() === 'enter' || event.key.toLowerCase() === 'space') {
						this.triggerHotspot(hotspot.TimedId);
					}
				});
				break;
		}

		_hotspotsComponent.el().querySelector(".mediasite-player__hotspots_container").appendChild(hotspotDOM);
		return hotspotDOM;
	}

	#renderAreaHotspot(hotspot, hotspotDOM) {
		hotspotDOM.style.setProperty('height', `${hotspot.Height * 100}%`);
		hotspotDOM.style.setProperty('width', `${hotspot.Width * 100}%`);
		hotspotDOM.setAttribute("role", "button");
		hotspotDOM.setAttribute("tabindex", "0");
		const hotspotDescription = `Hotspot with Left: ${Math.round(hotspot.X * 100)}% 
				Top: ${Math.round(hotspot.Y * 100)}% 
				Height: ${Math.round(hotspot.Height * 100)}% 
				Width: ${Math.round(hotspot.Width * 100)}%`;
		hotspotDOM.setAttribute("aria-label", hotspotDescription);

		//if the hotspot color or opacity aren't valid, use defaults
		const hotspotColor = hotspot.Color != null ? hotspot.Color : HOTSPOT_DEFAULT_COLOR;
		if (hotspot.HasNoFill) {
			hotspotDOM.style.setProperty('background-color', `transparent`);
			hotspotDOM.style.setProperty('border', `4px solid ${hotspotColor}`);
		} else {
			hotspotDOM.style.setProperty('background-color', `${hotspotColor}`);			
		}

		hotspot.Opacity > 0 ? hotspotDOM.style.setProperty('opacity', `${hotspot.Opacity}`) : hotspotDOM.style.setProperty('opacity', `${HOTSPOT_DEFAULT_OPACITY}`);		

		switch (hotspot.Shape) {
			case "Rectangle":
				hotspotDOM.classList.add('mediasite-player__hotspot-rectangle');
				break;
			case "Circle":
			default:
				hotspotDOM.classList.add('mediasite-player__hotspot-circle');
				break;
		}

		return hotspotDOM;
	}

	//For the button, since it can't be resized, we render a div to hold the button that is the size in the editor and then
	//have that parent hold the button div and justify it depending on it position on the page
	#renderButtonHotspot(hotspot, hotspotDOMHolder) {
		hotspotDOMHolder.classList.add('mediasite-player__hotspot-holder');

		hotspotDOMHolder.style.setProperty('height', `${hotspot.Height * 100}%`);
		hotspotDOMHolder.style.setProperty('width', `${hotspot.Width * 100}%`);

		const xAlignment = this.#getAlignmentValue(hotspot.X);
		const yAlignment = this.#getAlignmentValue(hotspot.Y);

		hotspotDOMHolder.style.setProperty('justify-content', xAlignment);
		hotspotDOMHolder.style.setProperty('align-items', yAlignment);

		const hotspotDOM = document.createElement('div');
		hotspotDOM.classList.add('mediasite-player__hotspot-button');
		hotspotDOM.setAttribute("role", "button");
		hotspotDOM.setAttribute("tabindex", "0");

		hotspotDOM.textContent = hotspot.DisplayText;

		const mainInteractionColors = getInteractionColorsFromColor(hotspot.Color, hotspot.SecondaryColor);
		hotspotDOM.style.setProperty("--main-background-color", `${mainInteractionColors.MainColor}`);
		hotspotDOM.style.setProperty("--main-background-hover-color", `${mainInteractionColors.HoverColor}`);
		hotspotDOM.style.setProperty('--main-background-active-color', `${mainInteractionColors.ActiveColor}`);
		hotspotDOM.style.setProperty('--main-secondary-color', `${mainInteractionColors.TextColor}`);
		hotspotDOM.style.setProperty('--main-secondary-hover-color', `${mainInteractionColors.HoverTextColor}`);
		hotspotDOM.style.setProperty('--main-secondary-active-color', `${mainInteractionColors.ActiveTextColor}`);

		hotspotDOM.style.fontSize = this.#convertTextSizeIntoPx(hotspot.TextSize);
		hotspotDOMHolder.appendChild(hotspotDOM);

		return hotspotDOMHolder;
	}

	#getAlignmentValue(offset) {
		const firstBreakPoint = .33;
		const secondBreakPoint = .67;

		if (offset <= firstBreakPoint) {
			return 'flex-start';
		} else if (offset <= secondBreakPoint) {
			return 'center';			
		} else {
			return 'flex-end';
		}		
	}

	#renderTextHotspot(hotspot, hotspotDOM) {
		hotspotDOM.style.setProperty('height', `${hotspot.Height * 100}%`);
		hotspotDOM.style.setProperty('width', `${hotspot.Width * 100}%`);

		hotspotDOM.classList.add('mediasite-player__hotspot-text');
		hotspotDOM.textContent = hotspot.DisplayText;
		hotspotDOM.style.setProperty('color', `${hotspot.Color}`);
		hotspotDOM.style.setProperty('--secondary-color', `${hotspot.SecondaryColor}`);
		hotspotDOM.style.setProperty('--text-alignment', `${hotspot.TextAlignment}`);

		hotspotDOM.style.fontSize = this.#convertTextSizeIntoPx(hotspot.TextSize);		

		return hotspotDOM;
	}

	#convertTextSizeIntoPx(fontSize) {
		switch (fontSize) {
			case "Small":
				return "12px";
			case "Medium":
				return "18px";
			case "Large":
				return "24px";
			default:
				return "12px";
		}
	}

	deleteHotspot(id) {
		const targetHotspot = _renderedHotspots[id];
		if (_isCurrentVideo360) {
			this.player.video360?.().removeObject(targetHotspot);
		} else {
			targetHotspot.remove();
		}
	}

	clear() {
		const timedEventIds = Object.keys(_renderedHotspots);

		for (const id of timedEventIds) {
			this.deleteHotspot(id);
		}
	}
}

export default HotspotsPlugin;